/**
 * Ein `datetime-local`-Feld in der Zeit der **Anlage** (HM-190, Regel 3).
 *
 * Das Feld kennt keine Zeitzone; `new Date("2026-03-29T02:30")` liest es in der
 * des Browsers. Die Anlage rechnet aber in ihrer eigenen — und wer am Laptop in
 * Lissabon „seit 14:00" für ein Haus in Berlin einträgt, meint 14:00 in Berlin.
 * Ein stiller Versatz von einer Stunde sähe später aus wie ein Zählerwechsel um
 * die falsche Zeit. Gelesen und vorbelegt wird deshalb in der Zeitzone der
 * Anlage, und zwar beides gleich: `max` ist „jetzt" in derselben Zeit, in der
 * die Eingabe gelesen wird.
 *
 * **Warum nicht `@hausmeter/timeseries`?** Dort steht die Zeitzonenrechnung,
 * und dort gehört sie hin. Diese App zieht Fachpakete aber nicht ins Bündel
 * (sie stehen als devDependency, siehe `lib/legal.ts`); die zwei Funktionen
 * hier sind deshalb eine Abschrift von `partsInZone` und `zonedPartsToUtc` —
 * mit `Intl`, ohne festen Versatz — und `site-time.test.ts` hält sie an
 * Sommerzeit-Grenzen gegen das Original. Ändert sich dort die Konvention für
 * die Sprungstunde, wird der Test rot.
 */

const PARTS = new Map<string, Intl.DateTimeFormat>();

function formatterFor(timeZone: string): Intl.DateTimeFormat {
  let formatter = PARTS.get(timeZone);
  if (formatter === undefined) {
    formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    PARTS.set(timeZone, formatter);
  }
  return formatter;
}

interface WallClock {
  readonly year: number;
  readonly month: number;
  readonly day: number;
  readonly hour: number;
  readonly minute: number;
  readonly second: number;
}

function wallClockOf(instant: Date, timeZone: string): WallClock {
  const parts = formatterFor(timeZone).formatToParts(instant);
  const get = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((part) => part.type === type)?.value ?? 0);
  return {
    year: get("year"),
    month: get("month"),
    day: get("day"),
    hour: get("hour"),
    minute: get("minute"),
    second: get("second"),
  };
}

/** Die Wanduhr eines Zeitpunkts, als wäre sie UTC — der Abstand zum Zeitpunkt ist der Versatz der Zone. */
function wallAsUtc(instant: Date, timeZone: string): number {
  const w = wallClockOf(instant, timeZone);
  return Date.UTC(
    w.year,
    w.month - 1,
    w.day,
    w.hour,
    w.minute,
    w.second,
    instant.getUTCMilliseconds(),
  );
}

const pad = (value: number, length = 2): string => String(value).padStart(length, "0");

/** „Jetzt" als Wert eines `datetime-local`-Felds, in der Zeit der Anlage (auf die Minute). */
export function siteLocalInputValue(instant: Date, timeZone: string): string {
  const w = wallClockOf(instant, timeZone);
  return `${pad(w.year, 4)}-${pad(w.month)}-${pad(w.day)}T${pad(w.hour)}:${pad(w.minute)}`;
}

const INPUT = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;

/**
 * Der Zeitpunkt, den ein `datetime-local`-Wert in der Zeit der Anlage meint —
 * `null`, wenn der Wert keiner ist.
 *
 * Wie `zonedPartsToUtc`: zwei Näherungsschritte, weil der Versatz vom Ergebnis
 * abhängt. Gibt es die Uhrzeit nicht (Sprungstunde im Frühjahr), ergibt sich
 * der Zeitpunkt unmittelbar nach dem Sprung; gibt es sie zweimal (Herbst), der
 * frühere — dieselbe Konvention wie dort.
 */
export function instantOfSiteLocalInput(value: string, timeZone: string): Date | null {
  const match = INPUT.exec(value);
  if (match === null) return null;
  const [, y, mo, d, h, mi, s] = match;
  const naive = Date.UTC(
    Number(y),
    Number(mo) - 1,
    Number(d),
    Number(h),
    Number(mi),
    Number(s ?? 0),
  );
  if (Number.isNaN(naive)) return null;
  const first = new Date(naive - (wallAsUtc(new Date(naive), timeZone) - naive));
  const result = new Date(naive - (wallAsUtc(first, timeZone) - first.getTime()));
  return Number.isNaN(result.getTime()) ? null : result;
}
